import type { ReactNode } from 'react';

import type { Placement } from '@floating-ui/react';
import type { ComponentWithChildren } from 'common/types/helpers';
import ConditionalWrapper from 'common/utils/conditionalWrapper';
import HelpText from 'ui/components/atoms/HelpText';
import InputError from 'ui/components/atoms/InputError';
import type { ColorName } from 'ui/styles/config/colors';

import * as Styled from './InputWrapper.styled';

export type InputWrapperProps = {
  label?: ReactNode;
  labelAsDiv?: boolean;
  labelColor?: ColorName;
  rightLabel?: ReactNode;
  errors?: string[];
  showErrorAsTooltip?: boolean;
  afterInput?: ReactNode;
  afterErrors?: ReactNode;
  help?: ReactNode;
  'data-test-id'?: string;
  'data-test-id-tooltip'?: string;
  'data-dd-action'?: string;
  fullWidth?: boolean;
  portal?: boolean;
  inverse?: boolean;
  className?: string;
  helpPlacement?: Placement;
};

export const InputWrapper: ComponentWithChildren<InputWrapperProps> = ({
  label,
  rightLabel,
  labelColor,
  errors = [],
  showErrorAsTooltip,
  help,
  afterInput,
  afterErrors,
  labelAsDiv,
  children,
  'data-test-id': dataTestId = 'input-wrapper',
  'data-test-id-tooltip': tooltipTestId = 'tooltip-text',
  'data-dd-action': ddActionName,
  fullWidth,
  portal,
  inverse,
  className,
  helpPlacement = 'top-start',
}) => {
  return (
    <ConditionalWrapper
      condition={!!label || !!rightLabel}
      wrapper={children => (
        <Styled.Label
          // This is for contenteditable inputs, labels do not work and
          // cause havoc
          $fullWidth={fullWidth}
          as={labelAsDiv ? 'div' : undefined}
          className={className}
          data-test-id={dataTestId}
        >
          <Styled.LabelWrapper
            $inverse={inverse}
            $labelColor={labelColor}
            data-test-id="label-container"
          >
            <HelpText
              className="help-text"
              data-test-id={tooltipTestId}
              help={help!}
              placement={helpPlacement}
              portal={portal}
            >
              {label}
            </HelpText>
            {rightLabel && <Styled.RightLabel>{rightLabel}</Styled.RightLabel>}
          </Styled.LabelWrapper>
          {children}
        </Styled.Label>
      )}
    >
      <>
        <ConditionalWrapper
          condition={!!afterInput}
          wrapper={children => (
            <Styled.InputWrapper
              $fullWidth={fullWidth}
              data-dd-action={ddActionName}
              data-test-id="input-with-end-component"
            >
              {children}
              {afterInput}
            </Styled.InputWrapper>
          )}
        >
          {children}
        </ConditionalWrapper>
        {!!errors?.length && (
          <Styled.ErrorWrapper>
            <InputError
              errors={errors}
              showErrorAsTooltip={showErrorAsTooltip}
            />
          </Styled.ErrorWrapper>
        )}
        {afterErrors}
      </>
    </ConditionalWrapper>
  );
};

export default InputWrapper;
